import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';

import BidForm from './BidForm';
import bidContextInitialState from './bidContextInitialState';
import Header from './Header';
import ROUTES from '../Routes/constants';
import Summary from '../Vdp/BidBox/Summary';
import History from '../Vdp/BidBox/History';
import Controls from '../Vdp/BidBox/Controls';
import { useOmpDealerContext } from '../Vdp/OmpDealerProvider';

import Alert from '../../_shared/components/Alert';
import BaseModal from '../../_shared/components/BaseModal';
import ConfirmModal from '../../_shared/components/ConfirmModal';
import bidErrors from '../../_shared/constants/errorCodes';
import { BUSINESS_UNIT } from '../../_shared/constants/omp';
import ReducerConstants from '../../_shared/constants/reducers';
import timeouts from '../../_shared/constants/timeouts';
import getSelectedDealerId from '../../_shared/helpers/getSelectedDealerId';
import stringToLiteral from '../../_shared/helpers/stringToLiteral';
import useMpBidTracking from '../../_shared/hooks/useMixpanelBidTracking';
import useManufacturerList from '../../_shared/hooks/vehicle/useManufacturerList';
import usePlaceBid from '../../_shared/hooks/usePlaceBid';
import usePlaceProxy from '../../_shared/hooks/usePlaceProxy';
import useTradeChange from '../../_shared/hooks/useTradeChange';
import convertToCurrency from '../../_shared/utils/convertToCurrency';
import getBidModalLogic from '../../_shared/utils/getBidModalLogic';
import getQuickBidIncrements from '../../_shared/utils/getQuickBidIncrements';
import isOmpPlatform from '../../_shared/utils/isOmpPlatform';
import {
  BidPlaceErrorCode,
  FetchAuctionErrorCodes,
  ListVariant,
  TradeState,
  TradeViewVariant
} from '../../_shared/types/trade';
import {
  BidConfirmationModalType,
  BidSource,
  BidStatus,
  BidStatusName,
  BidType,
  ListVariantSource
} from '../../_shared/types/auction';

import BidService from '../../services/BidService';
import ErrorService from '../../services/ErrorService';
import { bidSlice } from '../../redux/slices/bid';
import {
  updateSelectedTradeAuctionSummary,
  updateSelectedTradeUpcomingProxy
} from '../../redux/slices/ui/selectedTrades';

import { AutoWinDisclaimer } from './Styles';
import ToastType from '../../_shared/constants/toastTypes';
import TRToast from '../../_shared/helpers/TRToast';
import mixpanelConfig from '../../_shared/constants/mixpanelConfig';
import useAnalytics from '../../_shared/hooks/useAnalytics';
import bidPlaceErrorCodeToMessageId from '../../_shared/helpers/bidPlaceErrorCodeToMessageId';
import useUpdateBidContextWithApi from '../../_shared/hooks/trade/useUpdateBidContextWithApi';
import updateBidContextWithAuctionSummary from '../../_shared/helpers/updateBidContextWithAuctionSummary';

const MODAL_WIDTH = 467;
const VARIANT_MODAL = 'modal';

const propTypes = {
  onCurrentProxyUpdated: PropTypes.func,
  location: PropTypes.string,
  presetBidAmount: PropTypes.string
};

const defaultProps = {
  onCurrentProxyUpdated: undefined,
  location: '',
  presetBidAmount: ''
};

const BidModal = forwardRef((props, ref) => {
  const queryClient = useQueryClient();
  const track = useAnalytics();
  const {
    location,
    onCurrentProxyUpdated,
    presetBidAmount,
    handleOmpBidding,
    handleOmpBidModalClose,
    handleOmpBidModalOpen
  } = props;
  const bidService = BidService.get();
  // change name
  const [bidContext, setBidContext] = useState(bidContextInitialState);

  const {
    auctionSummary,
    bidList,
    bidStatus,
    presetBidAmount: presetBidAmountContext,
    currentBidAmount,
    currentProxyAmount,
    currentUpcomingProxy,
    id: tradeId,
    isAuctionEnded,
    isOutBidSameDealer,
    isOutBidSameDGN,
    isSettingProxy,
    mediaCounts,
    numberOfBids,
    tradeState,
    upcomingProxy,
    variant,
    hasVehicleHistoryReport,
    confirmation,
    highBid,
    outbidDealerGroup,
    outbidDealership
  } = bidContext;

  const [bidModalOpen, setBidModalOpen] = useState(false);

  // Expose bid state/method to parent component
  useImperativeHandle(ref, () => ({
    bidModalOpen,
    bidContext,
    setBidModalOpen,
    setBidContext
  }));
  const [
    liveBid,
    configurations,
    user,
    sseConnectionStatus,
    isAutoWinEnabled
  ] = useSelector(
    state => [
      state[ReducerConstants.LIVE_BIDS][tradeId],
      state[ReducerConstants.AUTH]?.system?.configurations,
      state[ReducerConstants.AUTH]?.user,
      state[ReducerConstants.SSE_CONNECTION_STATUS],
      state?.organizationDealer?.isAutoWinEnabled
    ],
    shallowEqual
  );
  const {
    isMultiOrgAndEnabled,
    ompBiddingDealer,
    validOmpDealerships,
    isOmpMultiOrgDealer
  } = useOmpDealerContext();
  const { data: manufacturers } = useManufacturerList();
  const [bidAmount, setBidAmount] = useState('');
  const [mpBidAmount, setMpBidAmount] = useState('');
  const [proxyAmount, setProxyAmount] = useState('');
  const [bidIncrements, setBidIncrements] = useState(null);
  const [proxyIncrements, setProxyIncrements] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [minBidAmount, setMinBidAmount] = useState(50);
  const [minProxyAmount, setMinProxyAmount] = useState(100);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [mpBidError, setMpBidError] = useState([]);
  const [trackBidFlag, setTrackBidFlag] = useState(false);
  const [trackProxyFlag, setTrackProxyFlag] = useState(false);
  const [bidType, setBidType] = useState('');
  const [trackHasBid, setTrackHasBid] = useState(true);
  const [trackShowConfirmModal, setTrackShowConfirmModal] = useState(false);
  const [trackBidErrorFlag, setTrackBidErrorFlag] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmModalLogic, setConfirmModalLogic] = useState([]);
  const modalRef = useRef();
  const dispatch = useDispatch();
  const isOmp = isOmpPlatform();
  const [selectedDealershipOption, setSelectedDealershipOption] = useState(
    validOmpDealerships?.find(dealership => dealership.isCurrent)
      ?.organizationId ||
      validOmpDealerships?.find(dealership => dealership.isDefault)
        ?.organizationId
  );
  const [hasInvalidDealer, setHasInvalidDealer] = useState(false);
  const updateBidContextWithApi = useUpdateBidContextWithApi({
    tradeId,
    isOmp,
    location,
    setBidContext,
    handleOmpBidding
  });

  useEffect(() => {
    if (bidModalOpen === true) {
      if (isOmp) {
        // Covers edge case, ensures confirmation modal which is driven by async logic does not stay open from the last time bid modal was open
        setIsConfirmationOpen(false);
      }
      if (handleOmpBidModalOpen) {
        handleOmpBidModalOpen(tradeId);
      }
    }
  }, [bidModalOpen, handleOmpBidModalOpen, isOmp, tradeId]);

  // Updates the bid list in OMP list view bid modal on initial load and when numberOfBids is updated
  useEffect(() => {
    const updateBidList = async () => {
      const bidListResponse = await bidService.getBidList(tradeId);
      setBidContext(prev => ({
        ...prev,
        bidList: bidListResponse
      }));
    };

    if (
      numberOfBids > 0 &&
      isOmp &&
      bidModalOpen &&
      location === TradeViewVariant.OMP_LIST
    ) {
      updateBidList();
    }
  }, [bidModalOpen, bidService, isOmp, location, numberOfBids, tradeId]);

  const getBidSource = () => {
    if (location === ListVariant.LIST || location === ListVariant.GRID) {
      return ListVariantSource[location];
    }
    if (location === TradeViewVariant.OMP_LIST) {
      return BidSource.LIST_VIEW;
    }
    return window.location.href.includes(ROUTES.BUYING)
      ? BidSource.MINI_VDP
      : BidSource.VDP_OVERLAY;
  };

  const dealerId = useMemo(
    () =>
      getSelectedDealerId(
        validOmpDealerships,
        ompBiddingDealer?.organizationId
      ),
    [ompBiddingDealer?.organizationId, validOmpDealerships]
  );

  useMpBidTracking({
    tradeDetails: bidContext,
    trackBidFlag,
    trackProxyFlag,
    trackBidErrorFlag,
    trackHasBid,
    trackShowConfirmModal,
    mpBidError,
    proxyAmount,
    numberOfBids,
    bidStatus,
    bidAmount: mpBidAmount,
    liveBid,
    tradeState,
    manufacturers,
    bidSource: getBidSource(),
    bidType,
    setMpBidError,
    setTrackBidFlag,
    setTrackProxyFlag,
    setTrackBidErrorFlag,
    setTrackHasBid,
    setTrackShowConfirmModal,
    confirmationMpTrackers: {
      confirmation,
      highBid,
      outbidDealerGroup,
      outbidDealership
    },
    bidUsers: bidList?.users,
    userId: user?.id,
    biddingDealerId: dealerId ? parseInt(dealerId, 10) : null,
    isOmp,
    ecsId: ompBiddingDealer?.organizationId,
    organizationName: ompBiddingDealer?.displayName
  });

  useTradeChange(tradeId, setBidContext);

  useEffect(() => {
    // In OMP, do not rely on topBidderSummary to determine if the user is outbidding same dealership or dealer
    if (liveBid && !isOmp) {
      const currentDealerId = isMultiOrgAndEnabled
        ? parseInt(
            ompBiddingDealer?.sourceInfo.find(
              source => source.businessUnit === BUSINESS_UNIT.TRADEREV
            )?.sourceId,
            10
          )
        : user?.companyId;
      const userDealerGroupIds =
        isMultiOrgAndEnabled && user?.multiOrgDealerGroupIds?.length
          ? user?.multiOrgDealerGroupIds
          : user?.dealerGroupIds;
      const isOutbidDealer =
        liveBid?.topBidder?.companyId &&
        liveBid?.topBidder?.companyId === currentDealerId &&
        user?.id !== liveBid?.topBidder?.id;
      const isSameDGN = liveBid?.topBidder?.dealerGroupIds?.some?.(id =>
        userDealerGroupIds?.includes(id)
      );
      const isOutbidDGN =
        liveBid?.topBidder?.dealerGroupIds?.length &&
        (isSameDGN ||
          (!isMultiOrgAndEnabled && liveBid?.topBidder?.isSameDealerGroup)) &&
        liveBid?.topBidder?.id !== user?.id;

      setBidContext(context => ({
        ...context,
        isOutBidSameDealer: isOutbidDealer,
        isOutBidSameDGN: !isOutbidDealer ? isOutbidDGN : undefined,
        auctionSummary: {
          ...context.auctionSummary,
          numBids: liveBid.bidCount,
          maxBid: liveBid.maxBidAmount,
          topBidderSummary: liveBid.topBidder
        }
      }));
    }
  }, [
    isOmp,
    auctionSummary?.topBidderSummary,
    isMultiOrgAndEnabled,
    liveBid,
    ompBiddingDealer?.sourceInfo,
    setBidContext,
    user?.companyId,
    user?.dealerGroupIds,
    user?.id,
    user?.multiOrgDealerGroupIds
  ]);

  // Create a ref of the Bid Modal when it opens
  const setRef = node => {
    if (node) modalRef.current = node;
  };

  // Focus on the Bid Modal ref
  const focusModal = () => {
    if (modalRef && modalRef.current) {
      modalRef.current.focus();
    }
  };

  // DRY toast() method to use the same options
  const toaster = useCallback(component => {
    if (component && component.props) {
      TRToast(component, {
        onClose: focusModal,
        onOpen: focusModal,
        autoClose:
          component.props.variant !== 'success'
            ? timeouts.ERROR
            : timeouts.INFO,
        containerId: ToastType.MAIN
      });
    }
  }, []);

  const onClose = useCallback(() => {
    if (
      [TradeState.active, TradeState.closing].includes(tradeState) &&
      !currentProxyAmount
    ) {
      setBidContext(context => ({ ...context, isSettingProxy: false }));
    }
    if (
      [TradeState.active, TradeState.closing].includes(tradeState) &&
      proxyAmount === currentBidAmount &&
      bidStatus === BidStatus.topBidder
    ) {
      setBidContext(context => ({
        ...context,
        isSettingProxy: false,
        currentProxyAmount: undefined
      }));
    }
    setBidModalOpen(false);
    if (handleOmpBidModalClose) {
      handleOmpBidModalClose();
    }
    setBidAmount('');
  }, [
    bidStatus,
    currentBidAmount,
    currentProxyAmount,
    handleOmpBidModalClose,
    proxyAmount,
    tradeState
  ]);

  const onProxySet = useCallback(
    ({ bid, proxy }) => {
      const newContext = { currentProxyAmount: proxy };
      if (tradeState === TradeState.upcoming) {
        newContext.currentUpcomingProxy = {
          bidAmount: Number.parseInt(bid, 10),
          proxyAmount: proxy
        };
      }

      if (onCurrentProxyUpdated) {
        onCurrentProxyUpdated({
          tradeId,
          bidAmount: Number.parseInt(bid, 10),
          proxyAmount: Number.parseInt(proxy, 10)
        });
      }

      setBidContext(context => ({
        ...context,
        ...newContext
      }));
    },
    [onCurrentProxyUpdated, tradeId, tradeState]
  );

  const auctionId = auctionSummary?.id;

  const bidCreate = usePlaceBid({
    auctionId,
    dealerId,
    isOmp,
    tradeId
  });
  const proxyCreate = usePlaceProxy({
    auctionId,
    dealerId,
    isOmp,
    tradeId
  });

  /**
   * Places the bid
   */
  const submitBidClick = useCallback(
    async (quickBidAmount, validateSameDealershipOrDGN = true) => {
      setFormSubmitting(true);

      const proxyBid = isSettingProxy ? proxyAmount : null;

      const onSuccessfulBid = async data => {
        setFormSubmitting(false);

        // Update Bid Context from /bid/create response
        updateBidContextWithAuctionSummary(setBidContext, {
          ...data?.auctionSummary,
          biddingDealerId: dealerId ? parseInt(dealerId, 10) : null
        });

        // Communicates bidding information to vehicle card if on OMP
        if (handleOmpBidding) {
          handleOmpBidding({
            tradeId,
            auctionSummary: {
              ...data?.auctionSummary,
              // To lock dealership dropdown in side rail VDP if user bids on omp bid modal
              biddingDealerId: dealerId ? parseInt(dealerId, 10) : null
            },
            source: location
          });
        }

        if (isSettingProxy) {
          // sends a proxy bid event to mixpanel
          setMpBidAmount(bidAmount);
          setTrackProxyFlag(true);
        } else {
          // sends a bid event to mixpanel
          setMpBidAmount(quickBidAmount || bidAmount);
          setTrackBidFlag(true);
        }

        // Ensures user hasn't been outbid by a proxy from a different user
        const successfullySetProxy =
          !!proxyAmount && proxyAmount > data?.auctionSummary?.maxBid;

        setBidAmount('');
        if (proxyAmount) {
          if (successfullySetProxy) {
            onProxySet({ bid: bidAmount, proxy: proxyAmount });
          } else {
            setProxyAmount('');
          }
          onClose();
        }

        if (
          location === TradeViewVariant.VDP &&
          window.location.href.includes(ROUTES.BUYING)
        ) {
          dispatch(
            updateSelectedTradeAuctionSummary({
              tradeState,
              auctionSummary: data?.auctionSummary
            })
          );
        }

        // success message set (either with proxy amount set or bid placed)
        if (successfullySetProxy) {
          toaster(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage
                  id="vdp.success.proxySet"
                  values={{ proxyAmount: convertToCurrency(proxyAmount) }}
                />
              ]}
            />
          );
        } else {
          toaster(
            <Alert
              variant="success"
              messages={[<FormattedMessage id="vdp.success.bidPlaced" />]}
            />
          );
        }

        // Fetch latest bid list
        if (
          (location !== ListVariant.LIST ||
            (location === ListVariant.LIST &&
              !sseConnectionStatus?.isConnected)) &&
          // Prevents updating redux bid list when a bid is placed on OMP FederatedBidModal, redux bid list is only intended to be used for the VDP.
          // OMP FederatedBidModal should use the bidContext to update the bid list.
          !(isOmp && location === TradeViewVariant.OMP_LIST)
        ) {
          const bidListResponse = await bidService.getBidList(tradeId);
          dispatch(bidSlice.actions.setBidList(bidListResponse));
        }

        if (!sseConnectionStatus?.isConnected) {
          // Update Query with the data coming back from the bid/create API
          queryClient.setQueriesData('tradeList', queryData => {
            // Index of tradeSummaries matching the Trade ID
            const index = queryData?.pages[0]?.tradeSummaries.findIndex(
              trade => {
                return trade.id === tradeId;
              }
            );

            // Original trade details
            const tradeData = queryData?.pages[0]?.tradeSummaries[index];

            const newData = {
              ...tradeData,
              activeExpiryTime: data?.auctionSummary?.activeExpiryTime,
              auctionSummary: {
                ...tradeData.auctionSummary,
                ...data?.auctionSummary,
                biddingDealerId: dealerId ? parseInt(dealerId, 10) : null
              },
              bidStatus: data?.auctionSummary?.userBidStatus,
              currentBidAmount: data?.auctionSummary?.maxBid,
              currentProxyAmount: data?.auctionSummary?.proxyAmount,
              isOutBidSameDGN: false,
              isOutBidSameDealer: false,
              numberOfBids: data?.auctionSummary?.numBids,
              proxyAmount: data?.auctionSummary?.proxyAmount,
              variant: BidStatusName[data?.auctionSummary?.userBidStatus]
            };
            // update the cache
            const newQueryData = { ...queryData };
            newQueryData.pages[0].tradeSummaries[index] = newData;

            return newQueryData;
          });
        }
      };

      const onErrorBidding = error => {
        const { errorCode, message } = error;
        setFormSubmitting(false);

        // It is only possible for this statement to be true in OMP
        if (
          [
            BidPlaceErrorCode.OUTBID_DEALERSHIP,
            BidPlaceErrorCode.OUTBID_DGN
          ].includes(errorCode)
        ) {
          const onOmpConfirmModal = async () => {
            submitBidClick(quickBidAmount, false);
            setIsConfirmationOpen(false);
            setConfirmModalLogic([]);
            if (track) {
              track(mixpanelConfig.names.outbidModal, {
                is_modal_confirmed: true
              });
            }
          };

          const onOmpCancelModal = () => {
            setIsConfirmationOpen(false);
            setConfirmModalLogic([]);
            if (track) {
              track(mixpanelConfig.names.outbidModal, {
                is_modal_confirmed: false
              });
            }
          };

          const modalDetails = {
            confirmationTypesList: [
              errorCode === BidPlaceErrorCode.OUTBID_DEALERSHIP
                ? BidConfirmationModalType.outbidDealership
                : BidConfirmationModalType.outbidDGN
            ],
            confirmationModalLogic: setConfirmModalLogic,
            bid: bidAmount,
            proxy: proxyAmount,
            confirmModal: onOmpConfirmModal,
            cancelModal: onOmpCancelModal,
            quickBidAmount
          };
          getBidModalLogic(modalDetails);
          setIsConfirmationOpen(true);

          return;
        }
        const errorMessage = stringToLiteral(message);
        if (errorMessage) {
          ErrorService.trackError(error);
          if (
            [TradeState.active, TradeState.closing].includes(tradeState) &&
            currentProxyAmount
          ) {
            setIsEditing(false);
          }
          if (errorCode === bidErrors.AUCTION_ENDED) {
            setBidContext(context => ({
              ...context,
              isAuctionEnded: true
            }));
          }
          setBidAmount('');
          setProxyAmount('');
          // When these error codes occur, it's likely the user has outdated information on their screen because they haven't received an SSE event.
          if (FetchAuctionErrorCodes.includes(errorCode)) {
            track(mixpanelConfig.names.fetchAuctionSummary, {
              source: location,
              trade_id: tradeId,
              attempted_bid_amount: quickBidAmount,
              auction_id: auctionId,
              current_bid_amount: bidContext?.currentBidAmount
            });
            updateBidContextWithApi();
          }
          if (isOmp) {
            // We rely on FE to translate the error message for bid/place API
            const messageId = bidPlaceErrorCodeToMessageId(errorCode);
            toaster(
              <Alert
                variant="error"
                messages={[<FormattedMessage id={messageId} />]}
              />
            );
          } else {
            toaster(<Alert variant="error" messages={[errorMessage]} />);
          }
          mpBidError.push(errorMessage);
          setTrackBidErrorFlag(true);
        } else {
          toaster(
            <Alert
              variant="error"
              messages={[<FormattedMessage id="vdp.error.placingBid" />]}
            />
          );
        }
      };

      bidCreate.mutate(
        {
          bidAmount: quickBidAmount || bidAmount,
          proxyAmount: proxyBid,
          // Only validate same dealership & DGN using backend response in OMP, and NOT TradeRev
          validateSameDealershipOrDGN: isOmp && validateSameDealershipOrDGN
        },
        { onSuccess: onSuccessfulBid, onError: onErrorBidding }
      );
    },
    [
      isSettingProxy,
      proxyAmount,
      bidCreate,
      bidAmount,
      isOmp,
      handleOmpBidding,
      location,
      sseConnectionStatus?.isConnected,
      dealerId,
      tradeId,
      onProxySet,
      onClose,
      dispatch,
      tradeState,
      toaster,
      bidService,
      queryClient,
      track,
      currentProxyAmount,
      mpBidError,
      auctionId,
      bidContext?.currentBidAmount,
      updateBidContextWithApi,
      setBidContext
    ]
  );

  /**
   * Places the proxy
   */
  const submitProxyClick = async () => {
    setFormSubmitting(true);

    const onSuccessfulProxy = () => {
      setFormSubmitting(false);
      onProxySet({ bid: bidAmount, proxy: proxyAmount });
      setBidContext(context => ({
        ...context,
        auctionSummary: {
          ...context.auctionSummary,
          biddingDealerId: dealerId
        },
        details: {
          ...context.details,
          upcomingProxy: {
            ...context.details?.upcomingProxy,
            proxyAmount
          }
        }
      }));

      // Communicates proxy information to vehicle card if on OMP
      if (handleOmpBidding) {
        handleOmpBidding({
          tradeId,
          auctionSummary: {
            upcomingProxyAmount: proxyAmount,
            upcomingBidAmount: bidAmount
          },
          source: location
        });
      }

      if (
        location === TradeViewVariant.VDP &&
        window.location.href.includes(ROUTES.BUYING)
      ) {
        dispatch(
          updateSelectedTradeUpcomingProxy({
            tradeState,
            upcomingProxy: {
              bidAmount,
              proxyAmount
            }
          })
        );
      }
      toaster(
        <Alert
          variant="success"
          messages={[
            <FormattedMessage
              id="vdp.success.proxySet"
              values={{ proxyAmount: convertToCurrency(proxyAmount) }}
            />
          ]}
        />
      );
      // Send a proxy bid event to mixpanel
      setMpBidAmount(bidAmount);
      setTrackProxyFlag(true);
      onClose();
    };

    const onErrorProxy = error => {
      const { message } = error;
      const errorMessage = stringToLiteral(message);

      setFormSubmitting(false);
      if (errorMessage) {
        ErrorService.trackError(error);
        toaster(<Alert variant="error" messages={[errorMessage]} />);
      } else {
        toaster(
          <Alert
            variant="error"
            messages={[<FormattedMessage id="vdp.error.placingProxy" />]}
          />
        );
      }
    };

    proxyCreate.mutate(
      {
        bidAmount,
        proxyAmount
      },
      { onSuccess: onSuccessfulProxy, onError: onErrorProxy }
    );
  };

  const isFirstBid = () => {
    return (
      (tradeState === TradeState.active &&
        (!bidStatus || bidStatus === BidStatus.none)) ||
      (tradeState === TradeState.upcoming && !currentProxyAmount)
    );
  };

  const isBidHigh = () => {
    const highBidThreshold = auctionSummary?.highBidThreshold;
    return (
      bidAmount >= highBidThreshold &&
      (!currentBidAmount || currentBidAmount < highBidThreshold)
    );
  };

  const isProxyHigh = () => {
    const highBidThreshold = auctionSummary?.highBidThreshold;
    return (
      proxyAmount >= highBidThreshold &&
      (!currentProxyAmount || currentProxyAmount < highBidThreshold)
    );
  };

  const onConfirmModal = async quickBidAmount => {
    setBidContext(context => ({
      ...context,
      outbidDealership: isOutBidSameDealer,
      outbidDealerGroup: isOutBidSameDGN,
      confirmation: isFirstBid()
    }));

    if (isFirstBid()) {
      setTrackShowConfirmModal(true);
      setTrackHasBid(true);
    }

    if (
      isSettingProxy &&
      ![TradeState.active, TradeState.closing].includes(tradeState)
    ) {
      await submitProxyClick();
    } else {
      await submitBidClick(quickBidAmount);
    }

    setFormSubmitting(false);
    setIsConfirmationOpen(false);
    setConfirmModalLogic([]);
  };

  const onCancelModal = () => {
    if (isFirstBid()) {
      setTrackShowConfirmModal(true);
      setTrackHasBid(false);
    }
    setBidAmount('');
    setProxyAmount('');
    setFormErrors({});
    setIsEditing(false);
    setFormSubmitting(false);
    setIsConfirmationOpen(false);
    setConfirmModalLogic([]);
  };

  const getConfirmationTypesList = quickBidAmount => {
    // Initial check for warning modals on open
    const confirmationTypes = [];

    // If user's first bid on trade card
    if (isFirstBid()) confirmationTypes.push(BidConfirmationModalType.legal);

    // If the bid is very high
    if (!quickBidAmount && isBidHigh()) {
      setBidContext(context => ({ ...context, highBid: true }));
      confirmationTypes.push(BidConfirmationModalType.overBidThreshold);
      mpBidError.push('bid_above_max_threshold');
    }
    if (isProxyHigh())
      confirmationTypes.push(BidConfirmationModalType.overProxyThreshold);

    if (isOutBidSameDealer) {
      // If there is already another bid from the same dealership
      confirmationTypes.push(BidConfirmationModalType.outbidDealership);
    } else if (isOutBidSameDGN) {
      confirmationTypes.push(BidConfirmationModalType.outbidDGN);
    }

    return confirmationTypes;
  };

  /**
   * Validation
   */
  const validateForm = quickBidAmount => {
    const {
      validationErrors: { bidValidationErrors, proxyValidationErrors },
      mpBidErrors
    } = bidService.validateForm({
      bidAmount,
      currentBidAmount,
      isSettingProxy,
      bidStatus,
      minBidAmount,
      proxyAmount,
      auctionSummary,
      currentProxyAmount,
      minProxyAmount,
      configurations,
      tradeState,
      quickBidAmount
    });

    let formValid =
      !(bidValidationErrors.length > 0 || proxyValidationErrors.length > 0) ||
      quickBidAmount;
    mpBidError.push(...mpBidErrors);
    setFormErrors({
      ERROR_BID: quickBidAmount ? false : bidValidationErrors.length > 0,
      ERROR_PROXY: proxyValidationErrors.length > 0
    });

    if (!ompBiddingDealer && isMultiOrgAndEnabled) {
      setHasInvalidDealer(true);
      formValid = false;
    } else {
      setHasInvalidDealer(false);
    }

    // TODO: Move this logic into the bid service, and restructure the return out of it
    if (formValid) {
      const modalDetails = {
        confirmationTypesList: getConfirmationTypesList(quickBidAmount),
        confirmationModalLogic: setConfirmModalLogic,
        bid: bidAmount,
        proxy: proxyAmount,
        confirmModal: onConfirmModal,
        cancelModal: onCancelModal,
        quickBidAmount
      };
      getBidModalLogic(modalDetails);
      if (getConfirmationTypesList(quickBidAmount)?.length !== 0)
        formValid = false;

      setIsConfirmationOpen(!formValid);
      // sends bid error event to mixpanel
      setTrackBidErrorFlag(true);

      return formValid;
    }

    // show only one error at a time
    toaster(
      <Alert
        variant="error"
        messages={[...bidValidationErrors, ...proxyValidationErrors]}
      />
    );

    // sends bid error event to mixpanel
    setTrackBidErrorFlag(true);

    return formValid;
  };

  /**
   * Bid Button Click
   */
  const handleBidClick = quickBidAmount => {
    if (proxyAmount) {
      setBidType(BidType.proxy);
    } else if (quickBidAmount) {
      setBidType(BidType.quickBid);
    } else {
      setBidType(BidType.manual);
    }
    const formValid = validateForm(quickBidAmount);
    if (!formValid) {
      return;
    }

    submitBidClick(quickBidAmount);
    setIsEditing(false);
  };

  /**
   * Upcoming proxy
   */
  const handleProxyClick = async () => {
    setBidType(BidType.proxy);
    const formValid = validateForm();
    if (!formValid) {
      return;
    }

    if ([TradeState.active, TradeState.closing].includes(tradeState)) {
      submitBidClick();
    } else {
      submitProxyClick();
    }
  };

  /**
   * Quick Bid
   */
  const onQuickBidButtonClick = bidIncrement => {
    const newAmount = auctionSummary?.maxBid
      ? auctionSummary?.maxBid + bidIncrement
      : auctionSummary?.minBidThreshold;

    setBidAmount(newAmount);
    handleBidClick(newAmount);
  };

  /**
   * Clear errors
   */
  const handleOnClose = () => {
    if (formErrors?.ERROR_BID) {
      setBidAmount('');
    }
    setFormErrors({});

    if (!currentBidAmount && !proxyAmount) {
      setBidAmount('');
    } else if (tradeState === TradeState.upcoming) {
      setBidAmount(currentUpcomingProxy?.bidAmount);
    }

    if (!proxyAmount) {
      setProxyAmount(currentProxyAmount);
    }

    onClose();
    setIsEditing(false);
  };

  const handleProxyToggleClick = () => {
    if ([TradeState.active, TradeState.closing].includes(tradeState)) {
      setBidAmount('');
      setProxyAmount('');
      setFormErrors({});
      if (isEditing) {
        setIsEditing(false);
      }
    }

    setBidContext(context => ({
      ...context,
      isSettingProxy: !isSettingProxy
    }));
  };

  useEffect(() => {
    const increment = [
      TradeState.upcoming,
      TradeState.active,
      TradeState.closing
    ].includes(tradeState)
      ? getQuickBidIncrements(
          currentBidAmount || auctionSummary?.minBidThreshold,
          configurations
        )
      : auctionSummary?.minBidThreshold;

    const startingAmount =
      [TradeState.active, TradeState.closing].includes(tradeState) &&
      currentBidAmount
        ? currentBidAmount + increment
        : auctionSummary?.minBidThreshold;

    setBidIncrements(increment);
    setMinBidAmount(startingAmount);
    if (
      currentBidAmount &&
      (bidStatus === BidStatus.topBidder || bidStatus === BidStatus.proxy)
    ) {
      setMinProxyAmount(startingAmount);
    } else {
      setMinProxyAmount(startingAmount + increment);
    }
  }, [
    auctionSummary?.minBidThreshold,
    bidStatus,
    configurations,
    currentBidAmount,
    tradeState
  ]);

  useEffect(() => {
    if ([TradeState.active, TradeState.closing].includes(tradeState)) {
      setProxyAmount(currentProxyAmount);
    }
  }, [currentProxyAmount, tradeState]);

  useEffect(() => {
    setBidAmount(presetBidAmount || presetBidAmountContext);
  }, [presetBidAmount, presetBidAmountContext]);

  useEffect(() => {
    setBidAmount(currentUpcomingProxy?.bidAmount);
    setProxyAmount(currentUpcomingProxy?.proxyAmount);
  }, [currentUpcomingProxy]);

  useEffect(() => {
    const amount = auctionSummary?.maxBid || 0;

    setBidIncrements(getQuickBidIncrements(amount, configurations));
  }, [configurations, auctionSummary?.maxBid]);

  useEffect(() => {
    if (!proxyAmount) {
      return;
    }

    setProxyIncrements(getQuickBidIncrements(proxyAmount, configurations));
  }, [configurations, proxyAmount]);

  useEffect(() => {
    if (
      [TradeState.active, TradeState.closing, TradeState.upcoming].includes(
        tradeState
      ) &&
      upcomingProxy?.proxyAmount
    ) {
      setProxyAmount(upcomingProxy?.proxyAmount);
      setBidContext(context => ({
        ...context,
        currentProxyAmount: upcomingProxy?.proxyAmount
      }));
    }
  }, [setBidContext, tradeState, upcomingProxy?.proxyAmount]);

  return (
    <BaseModal
      width={MODAL_WIDTH}
      padding="0"
      isOpen={bidModalOpen}
      variant={variant}
      isClickEscClose
      isClickOverlayClose
      onRequestClose={handleOnClose}
      setRef={setRef}
      overflow="initial"
      testId="bidModal-baseModal"
      hasParentSelector={isOmp && location === TradeViewVariant.VDP}
    >
      <Header
        onClose={handleOnClose}
        bidContext={bidContext}
        manufacturers={manufacturers}
        testId="bidModal-Header"
      />
      {[TradeState.active, TradeState.closing].includes(tradeState) && (
        <>
          <Summary
            variant={VARIANT_MODAL}
            bidContext={bidContext}
            testId="bidModal-summary"
          />
          <History
            variant={VARIANT_MODAL}
            bidContext={bidContext}
            testId="bidModal-history"
          />
        </>
      )}
      <BidForm
        bidAmount={bidAmount}
        bidContext={bidContext}
        bidIncrements={bidIncrements}
        bidStatus={bidStatus}
        currentBidAmount={currentBidAmount}
        currentProxyAmount={currentProxyAmount}
        formErrors={formErrors}
        hasInvalidDealer={hasInvalidDealer}
        isAuctionEnded={isAuctionEnded}
        isEditing={isEditing}
        isMultiOrgAndEnabled={isMultiOrgAndEnabled}
        minBidAmount={minBidAmount}
        minProxyAmount={minProxyAmount}
        onBidAmountChange={amount => {
          if (!amount) {
            setBidAmount('');
            return;
          }

          const intAmount = parseInt(amount, 10);
          if (Number.isNaN(intAmount)) {
            setBidAmount('');
          } else {
            setBidAmount(amount);
          }
        }}
        onProxyAmountChange={amount => {
          if (!amount) {
            setProxyAmount('');
            return;
          }

          const intAmount = parseInt(amount, 10);
          if (Number.isNaN(amount)) {
            setProxyAmount('');
          } else {
            setProxyAmount(intAmount);
          }
        }}
        onProxyToggleChange={handleProxyToggleClick}
        onSetIsEditingChange={val => {
          setIsEditing(val);
        }}
        proxyAmount={proxyAmount}
        proxyIncrements={proxyIncrements}
        selectedDealershipOption={selectedDealershipOption}
        setSelectedDealershipOption={setSelectedDealershipOption}
        validDealerships={validOmpDealerships}
      />
      {isSettingProxy && isAutoWinEnabled && !isOmpMultiOrgDealer && (
        // NOTE temporary fix disable autowin on omp for users with multiple dealerships
        // refer to https://dev.azure.com/karauctionservices/KAR-DMP/_workitems/edit/572181
        <AutoWinDisclaimer variant="success">
          <FormattedMessage id="auto.win.on.disclaimer" />
        </AutoWinDisclaimer>
      )}
      <Controls
        bidAmount={bidAmount}
        bidIncrements={bidIncrements}
        bidStatus={bidStatus}
        currentBidAmount={currentBidAmount}
        currentProxyAmount={currentProxyAmount}
        formSubmitting={formSubmitting}
        hasVehicleHistoryReport={hasVehicleHistoryReport}
        isAuctionEnded={isAuctionEnded}
        isEditing={isEditing}
        isSettingProxy={isSettingProxy}
        mediaCounts={mediaCounts}
        minBidAmount={auctionSummary?.minBidThreshold}
        numberOfBids={numberOfBids}
        onBidButtonClick={handleBidClick}
        onProxyButtonClick={handleProxyClick}
        onQuickBidButtonClick={onQuickBidButtonClick}
        proxyAmount={proxyAmount}
        selectedDealershipOption={selectedDealershipOption}
        tradeId={tradeId}
        tradeState={tradeState}
        validDealerships={validOmpDealerships}
        variant={TradeViewVariant.BID_MODAL}
      />
      <ConfirmModal
        location={location}
        isOpen={isConfirmationOpen}
        modalLogic={confirmModalLogic}
        isLoading={formSubmitting}
        loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
      />
    </BaseModal>
  );
});

BidModal.propTypes = propTypes;
BidModal.defaultProps = defaultProps;

export default BidModal;
